import maplibregl, { type Map as MlMap, Marker as MlMarker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import type { LatLng, MapMountOpts, MapProvider, ZoneLayer, ZoneMarker } from './provider'

const OSM_RASTER_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
      maxzoom: 19,
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
}

export class OsmProvider implements MapProvider {
  private map: MlMap | null = null
  private markers: MlMarker[] = []
  private readonly listeners: Record<string, Array<(arg: unknown) => void>> = {}

  async mount(el: HTMLElement, opts: MapMountOpts): Promise<void> {
    this.map = new maplibregl.Map({
      container: el,
      style: OSM_RASTER_STYLE,
      center: [opts.center.lng, opts.center.lat],
      zoom: opts.zoom,
      attributionControl: { compact: true },
    })
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }))
    await new Promise<void>((res) => this.map?.on('load', () => res()))
  }

  unmount(): void {
    for (const m of this.markers) m.remove()
    this.markers = []
    this.map?.remove()
    this.map = null
  }

  setMarkers(markers: ZoneMarker[]): void {
    if (!this.map) return
    for (const m of this.markers) m.remove()
    this.markers = []
    for (const z of markers) {
      const el = document.createElement('button')
      el.type = 'button'
      el.className =
        'flex h-8 items-center gap-1 rounded-full border-2 border-white bg-sky-600 px-2.5 text-xs font-semibold text-white shadow-md transition hover:bg-sky-700'
      el.textContent = z.label
      el.addEventListener('click', (e) => {
        e.stopPropagation()
        this.emit('zone-click', z.id)
      })
      const marker = new MlMarker({ element: el, anchor: 'bottom' })
        .setLngLat([z.lng, z.lat])
        .addTo(this.map)
      this.markers.push(marker)
    }
  }

  setZones(layer: ZoneLayer): void {
    if (!this.map) return
    const sourceId = `zone-${layer.id}`
    const fillLayerId = `${sourceId}-fill`
    const lineLayerId = `${sourceId}-line`
    if (this.map.getLayer(fillLayerId)) this.map.removeLayer(fillLayerId)
    if (this.map.getLayer(lineLayerId)) this.map.removeLayer(lineLayerId)
    if (this.map.getSource(sourceId)) this.map.removeSource(sourceId)

    this.map.addSource(sourceId, {
      type: 'geojson',
      data: layer.geoJson as unknown as GeoJSON.FeatureCollection,
    })
    // Use per-feature fillColor when present, else fall back to layer-level color.
    const fallback = layer.fillColor ?? '#0284c7'
    this.map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': [
          'case',
          ['has', 'fillColor'],
          ['get', 'fillColor'],
          fallback,
        ] as unknown as string,
        'fill-opacity': 0.35,
      },
    })
    this.map.addLayer({
      id: lineLayerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': [
          'case',
          ['has', 'fillColor'],
          ['get', 'fillColor'],
          fallback,
        ] as unknown as string,
        'line-width': 2,
      },
    })
    this.map.on('click', fillLayerId, (e) => {
      const id = e.features?.[0]?.properties?.id
      if (typeof id === 'string') this.emit('zone-click', id)
    })
  }

  setCenter(center: LatLng, zoom?: number): void {
    if (!this.map) return
    this.map.flyTo({
      center: [center.lng, center.lat],
      zoom: zoom ?? this.map.getZoom(),
    })
  }

  on(event: 'zone-click', handler: (zoneId: string) => void): void {
    ;(this.listeners[event] ??= []).push(handler as (arg: unknown) => void)
  }

  private emit(event: string, arg: unknown): void {
    for (const fn of this.listeners[event] ?? []) fn(arg)
  }
}
